import { ColDef } from "ag-grid-community";
import { ModalProps, Modals } from "..";
import { Buttons } from "../../../atoms/Buttons";
import { InputSelect } from "../../../atoms/Inputs/InputSelect";
import { Grid } from "../../Grid";
import * as S from "./ModalAlarm.style";
import { Legend } from "../../Legends";
import { FormEvent, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GET_STATUS_ALRAM } from "../../../../common/query-keys";
import { useRecoilValue } from "recoil";
import { currentShipState } from "../../../../common/states/ship";
import { request } from "../../../../common/api";
import { Status } from "../../../../common/types/status";
import useStatusSearchInputData from "../../../../common/hooks/useStatusSearchInputData";
import { SearchBar } from "../../SearchBar";
import { LedLegendItem } from "../../../atoms/LegendItem/LedLegendItem";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../atoms/Loading";

interface AlarmModalProps extends ModalProps {
  statusTime: string | undefined;
}

export interface StatusSearchInputs {
  steShiptypeNo?: string;
  steShiptypeName?: string;
  steShipNo?: string;
  steShipName?: string;
  steMcyName?: string;
  ssrMachine?: string;
  steLowMcyName?: string;
  steSensorName?: string;
}

export const ModalAlarm = ({ setModalShow, statusTime }: AlarmModalProps) => {
  const currentShip = useRecoilValue(currentShipState);
  const navigate = useNavigate();

  const searchDefaultValues = {
    steShiptypeNo: currentShip.shipTypeNo,
    steShiptypeName: currentShip.shipTypeName,
    steShipNo: currentShip.shipNo,
    steShipName: currentShip.shipName,
  };

  const {
    search,
    machinerySelectOptions,
    lowMachinerySelectOptions,
    sensorSelectOptions,
    handleMcySelectChange,
    handleLowMcySelectChange,
    handleSensorSelectChange,
  } = useStatusSearchInputData(searchDefaultValues);

  const [searchInputs, setSearchInputs] =
    useState<StatusSearchInputs>(searchDefaultValues);

  const [countCritical, setCountCritical] = useState<number>(0);
  const [countWarning, setCountWarning] = useState<number>(0);

  const { data: statusAlaramListData, isLoading } = useQuery({
    queryKey: [GET_STATUS_ALRAM, searchInputs],
    queryFn: async () => {
      const result = await request<Status[]>({
        method: "GET",
        url: "/state-alarm/modal",
        params: searchInputs,
      });

      const criticalData = result.filter((cur: Status) => {
        return cur.steAlarmType === 2;
      });

      const warningData = result.filter((cur: Status) => {
        return cur.steAlarmType === 1;
      });

      setCountWarning(warningData.length);
      setCountCritical(criticalData.length);

      return result;
    },
  });

  /** 검색 조건 검색 버튼 */
  const onSearchHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSearchInputs(search);
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "진단레벨",
      field: "steAlarmType",
      cellRenderer: (e: any) => {
        if (e.value === 1) {
          return <LedLegendItem ledColor="yellow" title="" />;
        } else {
          return <LedLegendItem ledColor="red" title="" />;
        }
      },
      width: 80,
    },
    {
      headerName: "알람발생시간",
      field: "steAlarmTime",
      valueGetter: (params: any) =>
        dayjs(params.data.steAlarmTime).format("YYYY.MM.DD_HH:mm:ss"),
      width: 160,
    },
    {
      headerName: "함형",
      field: "steShiptypeName",
      width: 90,
    },
    {
      headerName: "함정",
      field: "steShipName",
      width: 90,
    },
    {
      headerName: "장비",
      field: "steMcyName",
      width: 90,
    },
    {
      headerName: "계통",
      field: "steLowMcyName",
      width: 90,
    },
    {
      headerName: "센서",
      field: "steSensorName",
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: "센서 코드",
      field: "steSensorCode",
      minWidth: 90,
      flex: 1,
    },
    {
      headerName: "알람 메시지",
      field: "steAlarmMsg",
      minWidth: 120,
      flex: 1,
    },
  ];

  const onCellClicked = (params: any) => {
    setModalShow(false);
    navigate(`/support/alarm/status`, {
      state: {
        search: {
          steShiptypeNo: params.data.steShiptypeNo,
          steShiptypeName: params.data.steShiptypeName,
          steShipNo: params.data.steShipNo,
          steShipName: params.data.steShipName,
          steMcyNo: params.data.steMcyName,
          steMcyName: params.data.steMcyName,
          steLowMcyNo: params.data.steLowMcyName,
          steLowMcyName: params.data.steLowMcyName,
          steSensorCode: params.data.steSensorCode,
          steSensorName: params.data.steSensorName,
          steAlarmType: params.data.steAlarmType,
          ssrSignal: params.data.ssrSignal,
          // startDate: dayjs(params.data.steAlarmTime).format(
          //   "YYYY-MM-DD HH:mm:ss"
          // ),
          // endDate: dayjs(params.data.steAlarmTime).format(
          //   "YYYY-MM-DD HH:mm:ss"
          // ),
        },
      },
    });
  };

  return (
    <Modals
      modalTitle="상태진단 알람 현황"
      setModalShow={setModalShow}
      modalButtons={
        <Buttons
          type="button"
          size="md"
          layout="secondary"
          label="닫기"
          onClick={() => setModalShow(false)}
        />
      }
    >
      <>
        <S.ModalAlarmStatusBox>
          <S.ModalAlarmStatusTitle>
            <div className="title-box">
              상태진단 알람 <span>{statusAlaramListData?.length}</span>
            </div>
            <Legend title="Update time" type="uptime">
              <>{statusTime}</>
            </Legend>
          </S.ModalAlarmStatusTitle>
          <S.ModalAlarmStatus>
            <div>
              Critical <span className="critical">{countCritical}</span>
            </div>
            <div>
              Warning <span className="warning">{countWarning}</span>
            </div>
          </S.ModalAlarmStatus>
        </S.ModalAlarmStatusBox>
        <SearchBar
          onSearch={onSearchHandler}
          searchInput={
            <>
              <InputSelect
                name="steMcyName"
                size="md"
                placeholder="장비 선택"
                options={machinerySelectOptions}
                width={120}
                onChange={handleMcySelectChange}
                disabled={!search.steShipName}
                value={search.ssrMachine}
              />
              <InputSelect
                name="steLowMcyName"
                size="md"
                placeholder="계통 선택"
                options={lowMachinerySelectOptions}
                width={120}
                onChange={handleLowMcySelectChange}
                disabled={!search.steMcyName}
                value={search.steLowMcyName}
              />
              <InputSelect
                name="steSensorName"
                size="md"
                placeholder="센서 선택"
                options={sensorSelectOptions}
                width={120}
                onChange={handleSensorSelectChange}
                disabled={!search.steLowMcyName}
                value={search.steSensorName}
              />
            </>
          }
        />
        <S.ModalAlarmGridBox>
          {isLoading ? (
            <Loading />
          ) : (
            <Grid
              rowData={statusAlaramListData || []}
              columnDefs={columnDefs}
              paging={false}
              pagingShow={false}
              onCellClicked={onCellClicked}
            />
          )}
        </S.ModalAlarmGridBox>
      </>
    </Modals>
  );
};
